import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp } from 'lucide-react';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/authentication-and-role-selection');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const displayName = profile?.full_name || user?.email?.split('@')?.[0] || 'Usuário';
  const userRole = profile?.role === 'admin' ? 'Administrador' : 'Financeiro';

  // Navigation menu items
  const menuItems = [
    { name: 'Fluxo de Caixa', path: '/cash-flow-management-dashboard', icon: TrendingUp },
    { path: '/financial-dashboard-overview', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/negotiations-management-hub', label: 'Negociações', icon: 'Briefcase' },
    { path: '/enhanced-client-management-with-passengers', label: 'Clientes', icon: 'Users' },
    { path: '/expense-management-system', label: 'Despesas', icon: 'DollarSign' },
    { path: '/financial-reports-generator', label: 'Relatórios', icon: 'FileText' },
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="flex items-center justify-between h-16 px-6">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-accent">
              <Icon name="DollarSign" size={24} color="#FFFFFF" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-foreground">JR Wise Financial</h1>
              <p className="text-xs text-muted-foreground">Controle Financeiro</p>
            </div>
          </div>

          {/* Desktop Navigation Menu */}
          <nav className="hidden xl:flex items-center gap-1">
            {menuItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => navigate(item?.path)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span className="text-sm">{item?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="xl:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Icon name={showMobileMenu ? "X" : "Menu"} size={24} className="text-foreground" />
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground">{userRole}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-semibold">
                {displayName?.charAt(0)?.toUpperCase()}
              </div>
              <Icon name="ChevronDown" size={16} className="text-muted-foreground hidden md:block" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-xl py-2">
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-medium text-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-primary mt-1">{userRole}</p>
                </div>

                <button
                  onClick={() => {
                    navigate('/user-profile');
                    setShowUserMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
                >
                  <Icon name="User" size={16} />
                  <span>Meu Perfil</span>
                </button>

                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowUserMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2"
                >
                  <Icon name="Settings" size={16} />
                  <span>Configurações</span>
                </button>

                <div className="border-t border-border my-2" />

                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center gap-2"
                >
                  <Icon name="LogOut" size={16} />
                  <span>Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div className="xl:hidden border-t border-border bg-card">
          <nav className="px-6 py-4 space-y-2">
            {menuItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => {
                  navigate(item?.path);
                  setShowMobileMenu(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="text-sm font-medium">{item?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;